// ── Cover Section Editor ──────────────────────────────────────────────────────
// Background image upload, overlay opacity, title template

import { useState } from 'react';
import ImageUploader from './ImageUploader';
import { replaceVariables } from '../../lib/proposalDefaultData';

export default function CoverSection({ data, onChange, variables = {} }) {
  const cover = data || {};
  const [titleFocused, setTitleFocused] = useState(false);

  const update = (key, value) => {
    onChange({ ...cover, [key]: value });
  };

  // Only substitute variables that have actual values (skip empty strings)
  const nonEmptyVars = Object.fromEntries(
    Object.entries(variables).filter(([, v]) => v !== '' && v !== null && v !== undefined)
  );
  const template = cover.title_template || '{{project_name}} / PROPOSAL';
  const resolvedTitle = replaceVariables(template, nonEmptyVars);
  const hasResolved = resolvedTitle !== template && !resolvedTitle.includes('{{');

  return (
    <div className="space-y-4">
      {/* Background Image */}
      <ImageUploader
        value={cover.background_image_url}
        sourceType={cover.background_image_type || 'placeholder'}
        onChange={({ url, sourceType }) => {
          onChange({ ...cover, background_image_url: url, background_image_type: sourceType });
        }}
        onRemove={() => {
          update('background_image_url', '');
          update('background_image_type', 'placeholder');
        }}
        label="Background Image"
        aspectRatio="16:9"
        height="200px"
      />

      {/* Overlay Opacity */}
      <div>
        <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-1">
          Overlay Opacity
        </label>
        <div className="flex items-center gap-3">
          <input
            type="range"
            min={0}
            max={100}
            value={cover.overlay_opacity ?? 60}
            onChange={(e) => update('overlay_opacity', parseInt(e.target.value, 10))}
            className="flex-1 accent-[#c9a84c]"
          />
          <span className="text-sm font-mono text-[#1a1a2e] w-12 text-right">
            {cover.overlay_opacity ?? 60}%
          </span>
        </div>
        <p className="text-xs text-[#9ca3af] mt-1">
          Dark overlay on background image (0% = transparent, 100% = fully dark)
        </p>
      </div>

      {/* Title Template */}
      <div>
        <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-1">
          Title Template
        </label>
        <input
          type="text"
          // When focused: show the raw template for editing.
          // When blurred and resolved: show the resolved value as a preview.
          value={titleFocused ? (cover.title_template || '') : (hasResolved ? resolvedTitle : (cover.title_template || ''))}
          onChange={(e) => update('title_template', e.target.value)}
          onFocus={() => setTitleFocused(true)}
          onBlur={() => setTitleFocused(false)}
          placeholder="{{project_name}} / PROPOSAL"
          className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white transition-colors ${
            hasResolved && !titleFocused
              ? 'border-[#c9a84c]/60 text-[#1a1a2e] font-medium'
              : 'border-[#e5e4df]'
          }`}
        />
        <p className="text-xs text-[#9ca3af] mt-1">
          {titleFocused || !hasResolved
            ? <>Use {'{{variables}}'} like {'{{project_name}}'}, {'{{client_name}}'} for dynamic content.</>
            : <span className="text-[#c9a84c]">Click to edit template · showing resolved value</span>
          }
        </p>
      </div>

      {/* Cover Preview */}
      <div className="mt-4 rounded-lg overflow-hidden border border-[#e5e4df]" style={{ height: '200px' }}>
        <div
          className="relative w-full h-full flex flex-col items-center justify-center text-white"
          style={{
            background: cover.background_image_url
              ? `linear-gradient(rgba(26,26,46,${(cover.overlay_opacity ?? 60) / 100}), rgba(26,26,46,${(cover.overlay_opacity ?? 60) / 100})), url(${cover.background_image_url}) center/cover`
              : `linear-gradient(135deg, #1a1a2e 0%, #2d2d52 100%)`,
          }}
        >
          <h3
            className="text-2xl font-bold tracking-wide mb-2 px-4 text-center"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {resolvedTitle}
          </h3>
          <div className="w-16 h-0.5 bg-[#c9a84c] mb-3" />
          <p className="text-xs text-white/60">Cover Preview</p>
        </div>
      </div>
    </div>
  );
}
